import React, { useState, useEffect, useRef } from 'react';
import { Image, Upload, Save, RefreshCw, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSiteConfig = ({ embedded = false }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingSection, setUploadingSection] = useState(null);
  const fileInputRef = useRef(null);
  
  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fitOptions = [
    { value: 'cover', label: 'Cubrir (recorta para llenar)' },
    { value: 'contain', label: 'Contener (muestra completa)' },
    { value: 'fill', label: 'Estirar (deforma)' },
    { value: 'none', label: 'Original (sin ajuste)' }
  ];

  const imageFields = [
    { id: 'landing_hero', label: '🏠 Imagen Hero del Landing Page', field: 'landing_hero_image', fitField: 'landing_hero_image_fit', description: 'Imagen de fondo del inicio (donde aparecen ustedes)' },
    { id: 'hero', label: '🖼️ Imagen Principal (Hero)', field: 'hero_image', fitField: 'hero_image_fit', description: 'Imagen grande de otras páginas' },
    // Quiénes Somos - Fotos individuales
    { id: 'about_deisy', label: '👩 Quiénes Somos - Deisy Fit', field: 'about_image_deisy', fitField: 'about_image_deisy_fit', description: 'Foto individual de Deisy para "Quiénes Somos"' },
    { id: 'about_karen', label: '👩 Quiénes Somos - Karen', field: 'about_image_karen', fitField: 'about_image_karen_fit', description: 'Foto individual de Karen para "Quiénes Somos"' },
    // Coaching - Fotos individuales
    { id: 'coaching_deisy', label: '💪 Coaching - Deisy Fit', field: 'coaching_image_deisy', fitField: 'coaching_image_deisy_fit', description: 'Foto de Deisy para página de Coaching 1:1' },
    { id: 'coaching_karen', label: '💪 Coaching - Karen', field: 'coaching_image_karen', fitField: 'coaching_image_karen_fit', description: 'Foto de Karen para página de Coaching 1:1' },
    // Programas
    { id: 'programa_1', label: '📦 Imagen Nivel 1', field: 'programs_images.nivel_1', fitField: 'programs_images.nivel_1_fit', description: 'Imagen del programa Nivel 1 - Booty and Legs Lab' },
    { id: 'programa_2', label: '📦 Imagen Nivel 2', field: 'programs_images.nivel_2', fitField: 'programs_images.nivel_2_fit', description: 'Imagen del programa Nivel 2 - Evolution' },
    { id: 'programa_premium', label: '📦 Imagen Premium', field: 'programs_images.premium', fitField: 'programs_images.premium_fit', description: 'Imagen del programa Premium' },
  ];

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/site-config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const getFieldValue = (field) => {
    if (!config) return '';
    
    if (field.includes('.')) {
      const parts = field.split('.');
      let value = config[parts[0]]?.[parts[1]] || '';
      
      // Fallback: si es programs_images.premium y está vacío, usar nivel_3
      if (field === 'programs_images.premium' && !value) {
        value = config[parts[0]]?.['nivel_3'] || '';
      }
      
      return value;
    }
    return config[field] || '';
  };

  const updateFieldValue = (field, value) => {
    if (field.includes('.')) {
      const parts = field.split('.');
      setConfig({
        ...config,
        [parts[0]]: {
          ...config[parts[0]],
          [parts[1]]: value
        }
      });
    } else {
      setConfig({ ...config, [field]: value });
    }
  };

  const handleUpload = async (e, sectionId) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingSection(sectionId);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('section', sectionId);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/site-config/upload-image`, formData, {
        headers: {
          ...headers,
          
        }
      });
      
      toast.success('Imagen subida correctamente');
      fetchConfig(); // Refrescar configuración
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setUploadingSection(null);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/site-config`, config, { headers });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  const Wrapper = embedded ? 'div' : 'div';
  const wrapperClass = embedded ? 'space-y-6' : 'min-h-screen bg-[#09090b] py-12';

  return (
    <Wrapper className={wrapperClass}>
      <div className={embedded ? '' : 'max-w-4xl mx-auto px-4'}>
        {!embedded && (
          <div className="mb-8">
            <h1 className="text-3xl font-bold">Configuración del Sitio</h1>
            <p className="text-zinc-400">Personaliza las imágenes y textos de tu sitio web</p>
          </div>
        )}

        {/* Imágenes del sitio */}
        <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Image className="w-6 h-6 text-pink-600" />
              Imágenes del Sitio
            </h2>
            <Button
              onClick={async () => {
                try {
                  toast.info('Migrando imágenes a Cloudinary...');
                  const response = await axios.post(`${BACKEND_URL}/api/admin/site-config/migrate-to-cloudinary`, {}, { headers });
                  toast.success(`Migradas: ${response.data.migrated}, Ya en Cloudinary: ${response.data.already_cloudinary}`);
                  fetchConfig();
                } catch (error) {
                  toast.error('Error al migrar imágenes');
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Migrar a Cloudinary
            </Button>
          </div>

          <p className="text-sm text-zinc-400 mb-6 p-3 bg-zinc-800 rounded-lg">
            💡 Las imágenes se guardan en Cloudinary para mejor rendimiento. Al subir una nueva imagen, se actualizará automáticamente en todo el sitio.
          </p>

          <div className="space-y-6">
            {imageFields.map((field) => (
              <div key={field.id} className="border-b border-zinc-700 pb-6 last:border-0 last:pb-0">
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  <div className="w-32 h-24 bg-zinc-800 rounded-xl overflow-hidden flex-shrink-0">
                    {getFieldValue(field.field) ? (
                      <img
                        src={getFieldValue(field.field).startsWith('http') 
                          ? getFieldValue(field.field)
                          : `${BACKEND_URL}${getFieldValue(field.field)}`
                        }
                        alt={field.label}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full flex items-center justify-center text-zinc-400 ${getFieldValue(field.field) ? 'hidden' : ''}`}>
                      <Image className="w-8 h-8" />
                    </div>
                  </div>

                  {/* Info y acciones */}
                  <div className="flex-1">
                    <h3 className="font-bold">{field.label}</h3>
                    <p className="text-sm text-zinc-500 mb-3">{field.description}</p>
                    
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleUpload(e, field.id)}
                        className="hidden"
                        id={`upload-${field.id}`}
                      />
                      <label
                        htmlFor={`upload-${field.id}`}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                          uploadingSection === field.id 
                            ? 'bg-pink-50 border-pink-300' 
                            : 'bg-zinc-900 border-zinc-700 hover:border-pink-300'
                        }`}
                      >
                        {uploadingSection === field.id ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Subiendo...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            <span className="text-sm">Subir imagen</span>
                          </>
                        )}
                      </label>
                      
                      {getFieldValue(field.field) && (
                        <button
                          onClick={() => updateFieldValue(field.field, '')}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          title="Eliminar imagen"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* URL manual */}
                    <div className="mt-3">
                      <Input
                        value={getFieldValue(field.field)}
                        onChange={(e) => updateFieldValue(field.field, e.target.value)}
                        placeholder="O pega una URL de imagen..."
                        className="text-sm rounded-lg"
                      />
                    </div>

                    {/* Selector de ajuste de imagen */}
                    {field.fitField && (
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-zinc-500 mb-1">Ajuste de imagen:</label>
                        <select
                          value={getFieldValue(field.fitField) || 'cover'}
                          onChange={(e) => updateFieldValue(field.fitField, e.target.value)}
                          className="w-full px-3 py-2 border border-zinc-700 rounded-lg text-sm bg-zinc-900 focus:border-pink-300 focus:outline-none"
                        >
                          {fitOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Textos del Hero */}
        <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 mb-6">
          <h2 className="text-xl font-bold mb-6">Textos del Hero (Landing Page)</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Título Principal</label>
              <Input
                value={config?.hero_title || ''}
                onChange={(e) => setConfig({ ...config, hero_title: e.target.value })}
                placeholder="TRANSFORMA TU CUERPO TU VIDA"
                className="rounded-xl"
              />
              <p className="text-xs text-zinc-500 mt-1">El título grande que aparece en el hero</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Subtítulo (etiqueta superior)</label>
              <Input
                value={config?.hero_subtitle || ''}
                onChange={(e) => setConfig({ ...config, hero_subtitle: e.target.value })}
                placeholder="Academia de Transformación"
                className="rounded-xl"
              />
              <p className="text-xs text-zinc-500 mt-1">La etiqueta rosa que aparece arriba del título</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Descripción (texto debajo del título)</label>
              <textarea
                value={config?.hero_description || ''}
                onChange={(e) => setConfig({ ...config, hero_description: e.target.value })}
                placeholder="Programas de entrenamiento especializados en glúteos y piernas diseñados por entrenadoras certificadas."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white placeholder:text-zinc-500 min-h-[100px] focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                rows={3}
              />
              <p className="text-xs text-zinc-500 mt-1">El párrafo descriptivo que aparece debajo del título principal</p>
            </div>
          </div>
        </div>

        {/* Información de Contacto */}
        <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 mb-6">
          <h2 className="text-xl font-bold mb-6">Información de Contacto</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Instagram Entrenadora 1</label>
              <Input
                value={config?.social_instagram_1 || ''}
                onChange={(e) => setConfig({ ...config, social_instagram_1: e.target.value })}
                placeholder="@deisyfit"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Instagram Entrenadora 2</label>
              <Input
                value={config?.social_instagram_2 || ''}
                onChange={(e) => setConfig({ ...config, social_instagram_2: e.target.value })}
                placeholder="@karenmarinob"
                className="rounded-xl"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Email de Contacto</label>
              <Input
                value={config?.contact_email || ''}
                onChange={(e) => setConfig({ ...config, contact_email: e.target.value })}
                placeholder="bootyandlegsacademy@gmail.com"
                className="rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* Configuración de Precios de Academia */}
        <div className="bg-gradient-to-r from-pink-600/20 to-purple-600/20 rounded-2xl p-6 border border-pink-500/30 mb-6">
          <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
            🎓 Venta Individual de Academia
          </h2>
          <p className="text-zinc-400 text-sm mb-6">
            Permite a los usuarios comprar acceso a todos los videos de la Academia sin necesidad de suscribirse a un programa
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Precio de Academia (USD)</label>
              <Input
                type="number"
                step="0.01"
                value={config?.academia_price || ''}
                onChange={(e) => setConfig({ ...config, academia_price: parseFloat(e.target.value) || 0 })}
                placeholder="19.99"
                className="rounded-xl bg-zinc-800 border-zinc-700"
              />
              <p className="text-xs text-zinc-500 mt-1">Precio único para acceso a todos los videos</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Habilitar venta individual</label>
              <button
                type="button"
                onClick={() => setConfig({ ...config, academia_enabled: !config?.academia_enabled })}
                className={`w-full px-4 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                  config?.academia_enabled !== false
                    ? 'bg-green-500 text-white'
                    : 'bg-zinc-800 text-zinc-400'
                }`}
              >
                {config?.academia_enabled !== false ? (
                  <>
                    <Check className="w-5 h-5" />
                    Habilitado
                  </>
                ) : (
                  <>
                    <X className="w-5 h-5" />
                    Deshabilitado
                  </>
                )}
              </button>
              <p className="text-xs text-zinc-500 mt-1">Muestra el banner de compra en la página Academia</p>
            </div>
          </div>
        </div>

        {/* Botón Guardar */}
        <Button
          onClick={saveConfig}
          disabled={saving}
          className="w-full bg-black hover:bg-zinc-800 text-white rounded-full py-6 font-bold"
        >
          {saving ? (
            <>
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Guardar Configuración
            </>
          )}
        </Button>
      </div>
    </Wrapper>
  );
};

export default AdminSiteConfig;
